import { TextProps } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { useUpdatePart } from "#modules/parts/view/useUpdatePart";
import { Text } from "#shared/view/components/Text";
import { TextInput } from "#shared/view/designSystem/inputs/TextInput";
import { Tooltip } from "#shared/view/designSystem/Tooltip";

type PartNameLabelProps = TextProps & {
  partName: string;
  partId?: string;
  editable: boolean;
};

const commonTextProps: TextProps = {
  truncate: "end",
  c: colors.grey?.[5],
  fz: fontSizes.small,
  ta: "start",
  maw: 300,
};

export const PartNameLabel = ({
  partName,
  partId,
  editable,
  style,
  ...props
}: PartNameLabelProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [localPartName, setLocalPartName] = useState(partName);

  const { updatePart } = useUpdatePart(partId);

  const enterEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setLocalPartName(partName);
    setIsEditing(false);
  };

  const saveReference = () => {
    if (localPartName.length === 0) {
      return;
    }

    if (!partId) {
      cancelEditing();
    }

    updatePart({
      id: partId!,
      payload: {
        name: localPartName,
      },
    });

    setIsEditing(false);
  };

  if (!editable) {
    return (
      <Tooltip label={partName}>
        <Text {...commonTextProps} {...props} onClick={(e) => e.stopPropagation()}>
          {partName}
        </Text>
      </Tooltip>
    );
  }

  if (!isEditing) {
    return (
      <Tooltip label={partName}>
        <Text
          {...commonTextProps}
          {...props}
          // oxlint-disable-next-line typescript/no-misused-spread
          style={{ ...style, cursor: "pointer" }}
          td="underline"
          onClick={(e) => {
            e.stopPropagation();
            enterEditing();
          }}
        >
          {partName}
        </Text>
      </Tooltip>
    );
  }

  return (
    <TextInput
      mb={4}
      autoFocus
      placeholder={t("quoteRequestLine.partReferencePlaceholder")}
      value={localPartName}
      onBlur={saveReference}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        setLocalPartName(e.target.value);
      }}
      onKeyUp={(e) => {
        if (e.key === "Enter") {
          saveReference();
        }
        if (e.key === "Escape") {
          cancelEditing();
        }
      }}
    />
  );
};
